import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { localNewsArticles, newsSources, newsCategories } from '../../../mocks/localNews';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const getSource = (id: string) => newsSources.find((s) => s.id === id);

function parseDate(dateStr: string): Date {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? new Date() : d;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CATEGORY_ICON_MAP: Record<string, string> = Object.fromEntries(
  newsCategories.filter((c) => c.id !== 'all').map((c) => [c.name, c.icon])
);

// Keywords to extract and their display labels
const KEYWORD_SEEDS = [
  'park', 'council', 'school', 'business', 'tech', 'jobs', 'safety',
  'transit', 'highway', 'environment', 'solar', 'library', 'theater',
  'restaurant', 'hospital', 'sports', 'renovation', 'grant', 'housing',
  'festival', 'police', 'trees', 'water', 'trail', 'downtown', 'youth',
  'arts', 'health', 'award', 'construction', 'community', 'funding',
];

interface TagCloudItem {
  keyword: string;
  count: number;
  weight: number; // 1–5 for font sizing
}

function buildTagCloud(articles: typeof localNewsArticles): TagCloudItem[] {
  const counts: Record<string, number> = {};
  articles.forEach((a) => {
    const text = `${a.title} ${a.excerpt} ${a.category}`.toLowerCase();
    KEYWORD_SEEDS.forEach((kw) => {
      if (text.includes(kw)) {
        counts[kw] = (counts[kw] ?? 0) + 1;
      }
    });
  });

  const entries = Object.entries(counts).filter(([, c]) => c > 0);
  if (entries.length === 0) return [];

  const max = Math.max(...entries.map(([, c]) => c));
  const min = Math.min(...entries.map(([, c]) => c));

  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([keyword, count]) => ({
      keyword,
      count,
      weight: min === max ? 3 : Math.round(1 + ((count - min) / (max - min)) * 4),
    }));
}

const WEIGHT_CLASSES: Record<number, string> = {
  1: 'text-[11px] px-2 py-1',
  2: 'text-[12px] px-2.5 py-1',
  3: 'text-[13px] px-3 py-1.5 font-medium',
  4: 'text-[14px] px-3 py-1.5 font-semibold',
  5: 'text-[15px] px-3.5 py-2 font-bold',
};

interface ArticleCardProps {
  article: typeof localNewsArticles[0];
}

function ArticleCard({ article }: ArticleCardProps) {
  const src = getSource(article.source);
  const date = parseDate(article.date);
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <Link
      to={`/news/${article.id}`}
      className="group flex gap-4 bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-teal-100 transition-all cursor-pointer"
    >
      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          {article.isBreaking && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full whitespace-nowrap">
              <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
              BREAKING
            </span>
          )}
          {src && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${src.color} whitespace-nowrap`}>
              <i className={src.icon}></i>
              {src.name}
            </span>
          )}
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{article.category}</span>
        </div>
        <h4 className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-teal-600 transition-colors leading-snug">
          {article.title}
        </h4>
        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{article.excerpt}</p>
        <p className="text-[11px] text-gray-400 mt-1 inline-flex items-center gap-1">
          <i className="ri-calendar-line"></i>
          {formattedDate}
        </p>
      </div>
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 self-center text-gray-300 group-hover:text-teal-500 transition-colors">
        <i className="ri-arrow-right-s-line text-lg"></i>
      </div>
    </Link>
  );
}

export default function NewsArchivePage() {
  useSEO({
    title: 'Roseville News Archive | Browse Local Stories by Date & Category | Roseville.org',
    description: 'Browse the complete Roseville.org news archive organized by year, month, and date. Search and filter past local news stories from Roseville and Placer County by topic and keyword.',
    keywords: 'Roseville news archive, past Roseville news, Roseville California news history, Placer County news archive, local news search',
    canonical: '/news/archive',
    ogImage: 'https://public.readdy.ai/ai/img_res/24334c809e63f512cd0eb7ed38156665.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville News Archive',
          description: 'Complete archive of local news stories from Roseville and Placer County, organized by date and category.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/news/archive`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Local News', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/news` },
            { '@type': 'ListItem', position: 3, name: 'News Archive', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/news/archive` },
          ],
        },
      ],
    },
  });

  const tree = useMemo(() => {
    const result: Record<number, Record<number, Record<number, typeof localNewsArticles>>> = {};
    [...localNewsArticles]
      .sort((a, b) => parseDate(b.date).getTime() - parseDate(a.date).getTime())
      .forEach((article) => {
        const d = parseDate(article.date);
        const y = d.getFullYear();
        const m = d.getMonth();
        const day = d.getDate();
        if (!result[y]) result[y] = {};
        if (!result[y][m]) result[y][m] = {};
        if (!result[y][m][day]) result[y][m][day] = [];
        result[y][m][day].push(article);
      });
    return result;
  }, []);

  const years = Object.keys(tree).map(Number).sort((a, b) => b - a);

  const [selectedYear, setSelectedYear] = useState<number>(years[0] ?? new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);

  const allCategories = useMemo(() => {
    const counts: Record<string, number> = {};
    localNewsArticles.forEach((a) => {
      counts[a.category] = (counts[a.category] ?? 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count, icon: CATEGORY_ICON_MAP[name] ?? 'ri-price-tag-3-line' }));
  }, []);

  const tagCloud = useMemo(() => buildTagCloud(localNewsArticles), []);

  const months = selectedYear && tree[selectedYear]
    ? Object.keys(tree[selectedYear]).map(Number).sort((a, b) => b - a)
    : [];

  const days = selectedYear && selectedMonth !== null && tree[selectedYear]?.[selectedMonth]
    ? Object.keys(tree[selectedYear][selectedMonth]).map(Number).sort((a, b) => b - a)
    : [];

  const displayedArticles = useMemo(() => {
    let articles: typeof localNewsArticles = [];

    if (selectedDay !== null && selectedMonth !== null) {
      articles = tree[selectedYear]?.[selectedMonth]?.[selectedDay] ?? [];
    } else if (selectedMonth !== null) {
      const dayMap = tree[selectedYear]?.[selectedMonth] ?? {};
      Object.values(dayMap).forEach((dayArticles) => articles.push(...dayArticles));
    } else {
      const monthMap = tree[selectedYear] ?? {};
      Object.values(monthMap).forEach((dayMap) => {
        Object.values(dayMap).forEach((dayArticles) => articles.push(...dayArticles));
      });
    }

    if (selectedCategories.size > 0) {
      articles = articles.filter((a) => selectedCategories.has(a.category));
    }

    // Keyword filter
    if (selectedKeyword) {
      const kw = selectedKeyword.toLowerCase();
      articles = articles.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.excerpt.toLowerCase().includes(kw) ||
          a.category.toLowerCase().includes(kw),
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      articles = articles.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q),
      );
    }

    return articles.sort((a, b) => parseDate(b.date).getTime() - parseDate(a.date).getTime());
  }, [tree, selectedYear, selectedMonth, selectedDay, searchQuery, selectedCategories, selectedKeyword]);

  const totalArticles = localNewsArticles.length;

  const breadcrumb = useMemo(() => {
    const parts: string[] = [String(selectedYear)];
    if (selectedMonth !== null) parts.push(MONTHS[selectedMonth]);
    if (selectedDay !== null) parts.push(String(selectedDay));
    return parts.join(' › ');
  }, [selectedYear, selectedMonth, selectedDay]);

  const handleYearSelect = (y: number) => {
    setSelectedYear(y);
    setSelectedMonth(null);
    setSelectedDay(null);
  };

  const handleMonthSelect = (m: number) => {
    setSelectedMonth(m === selectedMonth ? null : m);
    setSelectedDay(null);
  };

  const handleDaySelect = (d: number) => {
    setSelectedDay(d === selectedDay ? null : d);
  };

  const toggleCategory = (name: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleKeyword = (kw: string) => {
    setSelectedKeyword((prev) => (prev === kw ? null : kw));
  };

  const clearAllFilters = () => {
    setSelectedMonth(null);
    setSelectedDay(null);
    setSearchQuery('');
    setSelectedCategories(new Set());
    setSelectedKeyword(null);
  };

  const hasActiveFilters =
    selectedMonth !== null ||
    selectedDay !== null ||
    selectedCategories.size > 0 ||
    searchQuery.trim() ||
    selectedKeyword !== null;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20">
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-14">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Link to="/news" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">
                    Local News
                  </Link>
                  <i className="ri-arrow-right-s-line text-gray-600"></i>
                  <span className="text-teal-400 text-sm font-medium">Archive</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  News <span className="text-teal-400">Archive</span>
                </h1>
                <p className="text-gray-400 text-sm max-w-lg">
                  Browse all {totalArticles} archived stories from Roseville and Placer County, organized by year, month, and date.
                </p>
              </div>
              <div className="relative w-full md:w-72">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search archive..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Sidebar ── */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-24">

              {/* Year tabs */}
              <div className="px-5 pt-5 pb-3 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Year</p>
                <div className="flex flex-wrap gap-2">
                  {years.map((y) => {
                    const count = Object.values(tree[y] ?? {}).reduce(
                      (acc, dayMap) => acc + Object.values(dayMap).reduce((a, arr) => a + arr.length, 0),
                      0,
                    );
                    return (
                      <button
                        key={y}
                        onClick={() => handleYearSelect(y)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          selectedYear === y
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {y}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          selectedYear === y ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Month list */}
              {months.length > 0 && (
                <div className="px-5 py-4 border-b border-gray-100">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Month</p>
                  <div className="space-y-1">
                    {months.map((m) => {
                      const count = Object.values(tree[selectedYear]?.[m] ?? {}).reduce(
                        (acc, arr) => acc + arr.length, 0,
                      );
                      const isActive = selectedMonth === m;
                      return (
                        <button
                          key={m}
                          onClick={() => handleMonthSelect(m)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                            isActive
                              ? 'bg-teal-50 text-teal-700 font-semibold'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <i className={`ri-calendar-2-line text-xs ${isActive ? 'text-teal-500' : 'text-gray-400'}`}></i>
                            {MONTHS[m]}
                          </span>
                          <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${
                            isActive ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Day list */}
              {selectedMonth !== null && days.length > 0 && (
                <div className="px-5 py-4 border-b border-gray-100">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Date</p>
                  <div className="flex flex-wrap gap-1.5">
                    {days.map((d) => {
                      const count = tree[selectedYear]?.[selectedMonth]?.[d]?.length ?? 0;
                      const isActive = selectedDay === d;
                      const dateObj = new Date(selectedYear, selectedMonth, d);
                      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                      return (
                        <button
                          key={d}
                          onClick={() => handleDaySelect(d)}
                          title={`${MONTHS[selectedMonth]} ${d}, ${selectedYear} — ${count} article${count !== 1 ? 's' : ''}`}
                          className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl text-xs font-semibold transition-all cursor-pointer border ${
                            isActive
                              ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                              : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                          }`}
                        >
                          <span className={`text-[9px] font-medium ${isActive ? 'text-teal-200' : 'text-gray-400'}`}>{dayName}</span>
                          <span className="text-sm leading-none">{d}</span>
                          <span className={`text-[9px] mt-0.5 ${isActive ? 'text-teal-200' : 'text-gray-400'}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Category Filter Chips ── */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Filter by Topic</p>
                  {selectedCategories.size > 0 && (
                    <button
                      onClick={() => setSelectedCategories(new Set())}
                      className="text-[11px] text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {allCategories.map(({ name, count, icon }) => {
                    const isActive = selectedCategories.has(name);
                    return (
                      <button
                        key={name}
                        onClick={() => toggleCategory(name)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer border whitespace-nowrap ${
                          isActive
                            ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                        }`}
                      >
                        <i className={`${icon} text-[10px]`}></i>
                        {name}
                        <span className={`text-[10px] px-1 py-0.5 rounded-full font-semibold ${
                          isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Keyword / Tag Cloud ── */}
              <div className="px-5 py-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <i className="ri-price-tag-3-line text-gray-400"></i>
                    Keyword Cloud
                  </p>
                  {selectedKeyword && (
                    <button
                      onClick={() => setSelectedKeyword(null)}
                      className="text-[11px] text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 leading-relaxed">
                  {tagCloud.map(({ keyword, count, weight }) => {
                    const isActive = selectedKeyword === keyword;
                    return (
                      <button
                        key={keyword}
                        onClick={() => toggleKeyword(keyword)}
                        title={`${count} article${count !== 1 ? 's' : ''} mentioning "${keyword}"`}
                        className={`inline-flex items-center rounded-md transition-all cursor-pointer border capitalize ${WEIGHT_CLASSES[weight]} ${
                          isActive
                            ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                            : weight >= 4
                            ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 hover:border-amber-400'
                            : weight === 3
                            ? 'bg-stone-100 text-stone-700 border-stone-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-stone-100 hover:text-stone-700'
                        }`}
                      >
                        {keyword}
                        <span className={`ml-1 text-[9px] font-bold opacity-60`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
                  Larger tags appear more frequently across all stories.
                </p>
              </div>

            </div>
          </aside>

          {/* ── Main content ── */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb + active filters bar */}
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 shadow-sm">
                  <i className="ri-archive-line text-teal-500 text-sm"></i>
                  {breadcrumb}
                </span>
                {/* Active category chips */}
                {[...selectedCategories].map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-teal-50 border border-teal-200 rounded-lg text-xs font-medium text-teal-700"
                  >
                    <i className={`${CATEGORY_ICON_MAP[cat] ?? 'ri-price-tag-3-line'} text-[10px]`}></i>
                    {cat}
                    <button
                      onClick={() => toggleCategory(cat)}
                      className="ml-0.5 text-teal-400 hover:text-teal-700 cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
                {/* Active keyword chip */}
                {selectedKeyword && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
                    <i className="ri-price-tag-3-line text-[10px]"></i>
                    #{selectedKeyword}
                    <button
                      onClick={() => setSelectedKeyword(null)}
                      className="ml-0.5 text-amber-400 hover:text-amber-700 cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                )}
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-line"></i>
                    Clear all
                  </button>
                )}
              </div>
              <span className="text-sm text-gray-500 whitespace-nowrap">
                <strong className="text-gray-800">{displayedArticles.length}</strong> article{displayedArticles.length !== 1 ? 's' : ''}
                {searchQuery && <span> matching &ldquo;<strong>{searchQuery}</strong>&rdquo;</span>}
                {selectedKeyword && !searchQuery && <span> tagged <strong>#{selectedKeyword}</strong></span>}
              </span>
            </div>

            {displayedArticles.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-search-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-500 text-sm mb-4">
                  {searchQuery
                    ? 'Try a different search term.'
                    : selectedKeyword
                    ? `No stories match the keyword "${selectedKeyword}" for this period.`
                    : selectedCategories.size > 0
                    ? 'No stories match the selected topics for this period.'
                    : 'No stories archived for this period.'}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <ArticleGroups articles={displayedArticles} groupByDay={selectedDay === null} />
            )}

            <div className="mt-10 flex items-center justify-center">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Latest News
              </Link>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function ArticleGroups({
  articles,
  groupByDay,
}: {
  articles: typeof localNewsArticles;
  groupByDay: boolean;
}) {
  if (!groupByDay) {
    return (
      <div className="space-y-3">
        {articles.map((a) => (
          <ArticleCard key={a.id} article={a} />
        ))}
      </div>
    );
  }

  const groups: Record<string, typeof localNewsArticles> = {};
  articles.forEach((a) => {
    const d = parseDate(a.date);
    const key = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(a);
  });

  return (
    <div className="space-y-8">
      {Object.entries(groups).map(([dateLabel, dayArticles]) => (
        <div key={dateLabel}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
              <i className="ri-calendar-check-line text-teal-600 text-sm"></i>
            </div>
            <h2 className="text-sm font-bold text-gray-700">{dateLabel}</h2>
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {dayArticles.length} article{dayArticles.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-3">
            {dayArticles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
